// Bundled into vite.config.ts (i18n preload links): type-only `@/` imports only, no value imports (the config loader cannot resolve them).
// The leaf module, not the api barrel: tsconfig.node.json has no DOM lib for the barrel's routes.
import type { SupportedLanguage } from '@/libs/sdk/api/constant';

export type AppLocale = { name: string; lang: SupportedLanguage };

// Every UI locale the shared frontend ships (order = switcher order).
export const ALL_LOCALES: AppLocale[] = [
  { name: 'English', lang: 'en' },
  { name: '简体中文', lang: 'zh-cn' },
  { name: '繁體中文', lang: 'zh-hk' },
  { name: 'Español', lang: 'es' },
  { name: '日本語', lang: 'ja' },
  { name: 'Tiếng Việt', lang: 'vi' },
  { name: '한국어', lang: 'ko' },
  { name: 'Русский', lang: 'ru' },
  { name: 'Português', lang: 'pt' },
];

/**
 * Tenant locale allowlist from VITE_LOCALES ("zh-hk,en"), in allowlist order.
 * Unset/blank keeps every locale (all tenants without the variable behave as
 * before). Unknown codes are dropped; if nothing valid remains the full list
 * is returned so a typo can never leave the site without a language.
 */
export const parseLocaleAllowlist = (raw: string | undefined): AppLocale[] => {
  const codes = (raw ?? '')
    .split(',')
    .map(code => code.trim().toLowerCase())
    .filter(Boolean);
  const picked: AppLocale[] = [];
  for (const code of codes) {
    const locale = ALL_LOCALES.find(item => item.lang === code);
    if (locale && !picked.includes(locale)) picked.push(locale);
  }
  return picked.length ? picked : ALL_LOCALES;
};

// Configured default when it is allowed, otherwise the first allowed locale.
export const resolveDefaultLocale = (raw: string | undefined, locales: AppLocale[]): SupportedLanguage =>
  locales.find(item => item.lang === (raw || 'en').toLowerCase())?.lang ?? locales[0].lang;

/**
 * Match a browser/stored language against the allowed locales by primary
 * subtag. zh prefers the matching script (HK/TW -> traditional, else
 * simplified) and falls back to the other allowed Chinese; anything unmatched
 * lands on the default.
 */
export const detectLocale = (browser: string, locales: AppLocale[], defaultLocale: string): string => {
  const lng = browser.toLowerCase();
  const allowed = (lang: string) => locales.some(item => item.lang === lang);
  if (lng.startsWith('zh')) {
    const preferred = lng.includes('hk') || lng.includes('tw') ? ['zh-hk', 'zh-cn'] : ['zh-cn', 'zh-hk'];
    return preferred.find(allowed) ?? defaultLocale;
  }
  const match = locales.find(item => lng === item.lang || lng.startsWith(item.lang + '-'));
  return match ? match.lang : defaultLocale;
};

// A persisted locale outside the allowlist: any Chinese maps to the allowed
// Chinese (zh-cn -> zh-hk on DPIN), everything else falls back to the default.
// Empty (first visit) stays empty so browser detection can run.
export const sanitizeLocale = (stored: string, locales: AppLocale[], defaultLocale: string): string => {
  if (!stored || locales.some(item => item.lang === stored)) return stored;
  return stored.toLowerCase().startsWith('zh') ? detectLocale(stored, locales, defaultLocale) : defaultLocale;
};
